/**
 * iOS In-App Purchase Service
 * Handles subscription purchases, restoration, and validation
 */

import { Capacitor } from '@capacitor/core';
import ReceiptRefresh from '../plugins/receiptRefresh';

/**
 * Get the current app user ID from the JWT auth token.
 * Returns null if not logged in.
 */
const getCurrentAppUserId = (): string | null => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id ? String(payload.user_id) : null;
  } catch {
    return null;
  }
};

// Product IDs for App Store
export const IAP_PRODUCTS = {
  basic: 'com.wanderingsock.annual.basic',
  pro: 'com.wanderingsock.annual.pro',
  business: 'com.wanderingsock.annual.business',
} as const;

export type IAPProductId = typeof IAP_PRODUCTS[keyof typeof IAP_PRODUCTS];

// Module-level product cache — populated when .updated() fires with product objects from Apple
export const _productCache = new Map<string, any>();

export const areProductsAvailable = (): boolean => {
  const store = (window as any).CdvPurchase?.store;
  if (!store) return false;
  return _productCache.size > 0 || (store.products || []).length > 0;
};

// Prevent duplicate initialization
let _iapInitialized = false;

// Debug log of all .updated() calls
export const _updatedLog: string[] = [];

// CdvPurchase types (from cordova-plugin-purchase)
declare global {
  interface Window {
    CdvPurchase?: {
      store: any;
      ProductType: {
        PAID_SUBSCRIPTION: string;
      };
      Platform: {
        APPLE_APPSTORE: string;
      };
    };
  }
}

/**
 * Check if IAP is available (iOS native platform)
 */
export const isIAPAvailable = (): boolean => {
  return Capacitor.isNativePlatform() && 
         Capacitor.getPlatform() === 'ios' &&
         typeof window.CdvPurchase !== 'undefined';
};

/**
 * Initialize IAP store
 */
export const initializeIAP = async (): Promise<void> => {
  if (!isIAPAvailable()) {
    console.log('IAP not available on this platform');
    return;
  }

  if (_iapInitialized) {
    console.log('[IAP] Already initialized, skipping');
    return;
  }
  _iapInitialized = true;

  const { CdvPurchase } = window;
  if (!CdvPurchase) return;

  const { store, ProductType, Platform } = CdvPurchase;

  // Capture any native StoreKit errors for diagnosis
  store.error((err: any) => {
    const errStr = `STORE_ERR code=${err?.code} msg=${err?.message}`;
    console.error('[IAP]', errStr);
    _updatedLog.push(errStr);
  });

  // Register products
  store.register([
    {
      id: IAP_PRODUCTS.basic,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: IAP_PRODUCTS.pro,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: IAP_PRODUCTS.business,
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
  ]);

  // Set up event handlers
  store.when()
    .approved((transaction: any) => {
      console.log('Transaction approved:', transaction);
      console.log('Transaction state:', transaction.state);
      console.log('Transaction ID:', transaction.transactionId);
      console.log('Full transaction object:', JSON.stringify(transaction, null, 2));
      
      // Store the transaction for later receipt retrieval
      (window as any).__lastIAPTransaction = transaction;
      
      // For sandbox subscriptions, finish immediately without verify
      transaction.finish();
    })
    .finished(async (purchase: any) => {
      console.log('[IAP] Purchase finished:', purchase);
      console.log('[IAP] Full purchase object:', JSON.stringify(purchase, null, 2));
      
      // Store the purchase for later receipt retrieval
      (window as any).__lastIAPPurchase = purchase;
      
      // Auto-activate subscription in sandbox mode
      // Check if this is a subscription product (not the app itself)
      const productId = purchase.products?.[0]?.id;
      if (productId && productId.includes('annual')) {
        console.log('[IAP] Subscription purchase detected:', productId);
        
        const transactionId = purchase.transactionId;
        const processedKey = 'iap_processed_transactions';
        const processed = JSON.parse(localStorage.getItem(processedKey) || '[]');
        
        if (!processed.includes(transactionId)) {
          // Check current ownership
          const product = store.get(productId);
          if (product && product.owned) {
            const currentUserId = getCurrentAppUserId();
            
            // Record which app user made this purchase
            if (currentUserId) {
              const ownerKey = 'iap_purchase_owner';
              const owners: Record<string, string> = JSON.parse(localStorage.getItem(ownerKey) || '{}');
              // Only set owner if not already set (first purchaser wins)
              if (!owners[productId]) {
                owners[productId] = currentUserId;
                localStorage.setItem(ownerKey, JSON.stringify(owners));
              }
              
              // Only auto-activate if current user is the original purchaser
              if (owners[productId] !== currentUserId) {
                console.log('[IAP] Skipping auto-activate: current user', currentUserId, 'does not match purchaser', owners[productId]);
              } else {
                console.log('[IAP] New purchase detected, triggering sandbox activation');
                window.dispatchEvent(new CustomEvent('iap-auto-activate', { 
                  detail: { productId, owned: true } 
                }));
              }
            }
            
            // Mark as processed regardless
            processed.push(transactionId);
            localStorage.setItem(processedKey, JSON.stringify(processed));
          }
        } else {
          console.log('[IAP] Transaction already processed, skipping:', transactionId);
        }
      }
      
      // Also trigger the old event for backward compatibility
      window.dispatchEvent(new CustomEvent('iap-purchase-finished', { detail: purchase }));
    })
    .updated((item: any) => {
      const knownIds = Object.values(IAP_PRODUCTS) as string[];
      if (item?.id && knownIds.includes(item.id)) {
        _productCache.set(item.id, item);
        console.log('[IAP] .updated() cached product:', item.id);
      }
    });

  // Initialize the store
  await store.initialize([Platform.APPLE_APPSTORE]);
  console.log('[IAP] Initialized. products:', (store.products || []).length, 'cache:', _productCache.size);

  // Finish any stale pending transactions
  (store.transactions || []).forEach((t: any) => {
    if (t.state !== 'finished') t.finish();
  });
};

/**
 * Get product information from App Store
 */
export const getProducts = async (): Promise<any[]> => {
  if (!isIAPAvailable()) return [];

  const { CdvPurchase } = window;
  if (!CdvPurchase) return [];

  const { store } = CdvPurchase;
  
  return [
    store.get(IAP_PRODUCTS.basic),
    store.get(IAP_PRODUCTS.pro),
    store.get(IAP_PRODUCTS.business),
  ].filter(Boolean);
};

/**
 * Purchase a subscription
 */
export const purchaseSubscription = async (
  tier: 'basic' | 'pro' | 'business'
): Promise<{ success: boolean; error?: string }> => {
  if (!isIAPAvailable()) {
    return { success: false, error: 'IAP not available' };
  }

  const { CdvPurchase } = window;
  if (!CdvPurchase) {
    return { success: false, error: 'IAP not initialized' };
  }

  const { store, Platform } = CdvPurchase;
  const productId = IAP_PRODUCTS[tier];

  const product =
    _productCache.get(productId) ||
    (store.products || []).find((p: any) => p.id === productId) ||
    store.get(productId, Platform.APPLE_APPSTORE) ||
    store.get(productId);

  if (!product) {
    console.error('[IAP] Product not found:', productId);
    return { success: false, error: 'Product not available. Please ensure your app is up to date and try again.' };
  }

  try {
    const offer = product.getOffer();
    if (!offer) {
      return { success: false, error: 'No offer available for this product.' };
    }
    await store.order(offer);
    return { success: true };
  } catch (error: any) {
    console.error('[IAP] Purchase error:', error);
    return { success: false, error: error.message || 'Purchase failed. Please try again.' };
  }
};

/**
 * Request receipt refresh from Apple using native iOS API
 * This forces iOS to fetch the latest receipt from Apple's servers
 */
export const refreshReceipt = async (): Promise<{ success: boolean; error?: string }> => {
  if (!isIAPAvailable()) {
    return { success: false, error: 'IAP not available' };
  }

  try {
    console.log('Requesting receipt refresh from Apple using native API...');
    
    // Use native iOS receipt refresh
    const result = await ReceiptRefresh.refreshReceipt();
    
    if (result.success) {
      console.log('Native receipt refresh completed successfully');
      
      // Wait a bit for the receipt to be available in the plugin
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return { success: true };
    } else {
      console.log('Native receipt refresh returned false');
      return { success: false, error: 'Receipt refresh returned false' };
    }
  } catch (error: any) {
    console.error('Receipt refresh error:', error);
    return { success: false, error: error.message || 'Refresh failed' };
  }
};

/**
 * Restore previous purchases
 */
export const restorePurchases = async (): Promise<{ success: boolean; error?: string }> => {
  if (!isIAPAvailable()) {
    return { success: false, error: 'IAP not available' };
  }

  const { CdvPurchase } = window;
  if (!CdvPurchase) {
    return { success: false, error: 'CdvPurchase not initialized' };
  }

  const { store } = CdvPurchase;

  try {
    await store.restorePurchases();
    
    // Also try to refresh the receipt
    await refreshReceipt();
    
    return { success: true };
  } catch (error: any) {
    console.error('Restore error:', error);
    return { success: false, error: error.message || 'Restore failed' };
  }
};

/**
 * Get the latest receipt data for validation
 * Returns object with receipt and debug info for user-visible alerts
 */
export const getReceiptData = async (): Promise<{ receipt: string | null; debugInfo: string }> => {
  const debugMessages: string[] = [];
  
  if (!isIAPAvailable()) {
    return { receipt: null, debugInfo: 'IAP not available on this platform' };
  }

  const { CdvPurchase } = window;
  if (!CdvPurchase) {
    return { receipt: null, debugInfo: 'CdvPurchase not initialized' };
  }

  const { store } = CdvPurchase;
  
  console.log('=== Attempting to retrieve receipt ===');
  debugMessages.push('Starting receipt search...');
  
  // Method 1: Check last stored transaction/purchase from event handlers
  const lastTransaction = (window as any).__lastIAPTransaction;
  const lastPurchase = (window as any).__lastIAPPurchase;
  
  if (lastTransaction) {
    console.log('Checking stored transaction for receipt...');
    debugMessages.push('Found stored transaction');
    if (lastTransaction.nativeReceipt) {
      console.log('Found nativeReceipt in transaction');
      return { receipt: lastTransaction.nativeReceipt, debugInfo: 'Receipt found in transaction.nativeReceipt' };
    }
    if (lastTransaction.receipt) {
      console.log('Found receipt in transaction');
      return { receipt: lastTransaction.receipt, debugInfo: 'Receipt found in transaction.receipt' };
    }
    debugMessages.push('Transaction has no receipt property');
  }
  
  if (lastPurchase) {
    console.log('Checking stored purchase for receipt...');
    debugMessages.push('Found stored purchase');
    if (lastPurchase.nativeReceipt) {
      console.log('Found nativeReceipt in purchase');
      return { receipt: lastPurchase.nativeReceipt, debugInfo: 'Receipt found in purchase.nativeReceipt' };
    }
    if (lastPurchase.receipt) {
      console.log('Found receipt in purchase');
      return { receipt: lastPurchase.receipt, debugInfo: 'Receipt found in purchase.receipt' };
    }
    debugMessages.push('Purchase has no receipt property');
  }
  
  // Method 2: Get from store.applicationReceipt (iOS app receipt)
  if (store.applicationReceipt) {
    console.log('Found store.applicationReceipt');
    return { receipt: store.applicationReceipt, debugInfo: 'Receipt found in store.applicationReceipt' };
  }
  debugMessages.push('No store.applicationReceipt');
  
  // Method 3: Get from localReceipts array
  const receipts = store.localReceipts || [];
  if (receipts.length > 0) {
    console.log('Found localReceipts:', receipts.length);
    debugMessages.push(`Found ${receipts.length} localReceipts`);
    const latestReceipt = receipts[receipts.length - 1];
    if (latestReceipt.nativeReceipt) {
      console.log('Using nativeReceipt from localReceipts');
      return { receipt: latestReceipt.nativeReceipt, debugInfo: 'Receipt found in localReceipts[].nativeReceipt' };
    }
    if (latestReceipt.data) {
      console.log('Using data from localReceipts');
      return { receipt: latestReceipt.data, debugInfo: 'Receipt found in localReceipts[].data' };
    }
    debugMessages.push('localReceipts have no receipt data');
  } else {
    debugMessages.push('No localReceipts');
  }
  
  // Method 4: Check all products for receipts
  const products = [
    store.get(IAP_PRODUCTS.basic),
    store.get(IAP_PRODUCTS.pro),
    store.get(IAP_PRODUCTS.business),
  ].filter(Boolean);
  
  const ownedProducts = products.filter(p => p.owned);
  if (ownedProducts.length > 0) {
    debugMessages.push(`Found ${ownedProducts.length} owned products: ${ownedProducts.map(p => p.id).join(', ')}`);
  } else {
    debugMessages.push('No owned products found');
  }
  
  for (const product of products) {
    if (product.owned && product.transaction) {
      console.log('Found owned product with transaction:', product.id);
      if (product.transaction.nativeReceipt) {
        console.log('Using nativeReceipt from product transaction');
        return { receipt: product.transaction.nativeReceipt, debugInfo: `Receipt found in ${product.id}.transaction.nativeReceipt` };
      }
      if (product.transaction.receipt) {
        console.log('Using receipt from product transaction');
        return { receipt: product.transaction.receipt, debugInfo: `Receipt found in ${product.id}.transaction.receipt` };
      }
    }
  }
  
  const finalDebug = debugMessages.join('\n');
  console.log('❌ No receipt found via any method');
  console.log('Debug info:', finalDebug);
  
  return { receipt: null, debugInfo: finalDebug };
};

/**
 * Validate receipt with backend
 */
export const validateReceipt = async (receiptData: string): Promise<{
  success: boolean;
  tier?: string;
  error?: string;
}> => {
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    // Import API_BASE_URL from config to use consistent backend detection
    const API_BASE_URL = await import('../config').then(m => m.default);

    const response = await fetch(`${API_BASE_URL}/api/payment/ios/validate-receipt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ receipt: receiptData }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Validation failed' };
    }

    return { success: true, tier: data.tier };
  } catch (error: any) {
    console.error('Receipt validation error:', error);
    return { success: false, error: error.message || 'Validation failed' };
  }
};
